import { ZuiBaseElement } from '@zywave/zui-base';
import { findAssignedElement } from '@zywave/zui-base/dist/utils/find-assigned-element.js';
import { html } from 'lit';
import { property, query } from 'lit/decorators.js';
import { style } from './zui-button-css.js';
import type { PropertyValues } from 'lit';

const clickKeys = ['Space', 'Enter'];
const anchorNodeName = 'a';

/**
 * A Zywave-standard button.
 *
 * @element zui-button
 *
 * @slot - Default, unnamed slot; for inserting text, `<a>`, `<button>`, and `<zui-icon>` elements into `<zui-button>`
 *
 * @csspart button - The button container inside `<zui-button>`; this is exposed as a CSS shadow part and can be accessed with `::part(button)`
 *
 * @cssprop [--zui-button-icon-color=currentColor] - (all types): slotted `zui-icon` fill color
 * @cssprop [--zui-button-text-color=#fff] - (`primary` / default / `secondary`): text color
 * @cssprop [--zui-button-color=var(--zui-blue)] - (`primary` / default): button background color
 * @cssprop [--zui-button-hover-color=var(--zui-blue-400)] - (`primary` / default): hover state button background color
 * @cssprop [--zui-button-text-hover-color=var(--zui-button-text-color)] - (`primary` / default): hover state text color
 * @cssprop [--zui-button-focus-color=var(--zui-blue-400)] - (`primary` / default): focus state button background color
 * @cssprop [--zui-button-text-focus-color=var(--zui-button-text-color)] - (`primary` / default): focus state text color
 * @cssprop [--zui-button-active-color=var(--zui-blue-600)] - (`primary` / default): active state background color
 * @cssprop [--zui-button-secondary-box-shadow=var(--zui-blue)] - (`secondary`): button outline color
 * @cssprop [--zui-button-secondary-box-shadow-hover=var(--zui-blue-400)] - (`secondary`): hover state button outline color
 * @cssprop [--zui-button-secondary-box-shadow-focus=var(--zui-blue-400)] - (`secondary`): focus state button outline color
 * @cssprop [--zui-button-secondary-box-shadow-active=var(--zui-blue-600)] - (`secondary`): active state button outline color
 * @cssprop [--zui-link-button-text-color=var(--zui-blue)] - (`link`): text color
 * @cssprop [--zui-link-button-hover-color=var(--zui-gray-100)] - (`link`): hover state button background color
 * @cssprop [--zui-link-button-focus-color=var(--zui-blue)] - (`link`): focus state button outline color
 * @cssprop [--zui-link-button-active-color=var(--zui-gray-200)] - (`link`): active state button background color
 * @cssprop [--zui-button-width=2.25rem (36px)] - (`icon-only`): force an exact width for `icon-only` button types
 **/
export class ZuiButton extends ZuiBaseElement {
  /**
   * Use this attribute to set the type of button dropdown you want. See <a href="https://booster.zywave.dev/design-system/components/buttons/">Buttons</a> for button style variations.
   * @type { primary | secondary | link }
   */
  @property({ type: String, reflect: true })
  type: ZuiButtonType = 'primary';

  /**
   * Use this attribute when you want an icon-only button
   */
  @property({ type: Boolean, reflect: true })
  icon = false;

  /**
   * Use this attribute when you want to right-align an icon in a button with text
   */
  @property({ type: Boolean, reflect: true, attribute: 'icon-right' })
  iconRight = false;

  /**
   * Use this attribute when you want the button to span the whole width of its parent container
   */
  @property({ type: Boolean, reflect: true })
  block = false;

  /**
   * Use this attribute when you want the button to stand out due to its destructive action
   */
  @property({ type: Boolean, reflect: true })
  danger = false;

  /**
   * Use this attribute when you want to disable `<zui-button>`
   */
  @property({ type: Boolean, reflect: true })
  disabled = false;

  @query('slot')
  _slotEl: HTMLSlotElement;

  @query('div')
  _wrapperDivEl: HTMLElement;

  get _slottedControl() {
    return findAssignedElement(this._slotEl, 'a,button');
  }

  get _hasWrappedAnchor() {
    return this.parentElement ? this.parentElement.nodeName.toLowerCase() === anchorNodeName : false;
  }

  firstUpdated() {
    this._setTabIndex();
    this.setAttribute('role', 'button');
    if (this._slottedControl) {
      this._slottedControl.tabIndex = -1;
    }

    this.addEventListener('keydown', (e) => {
      this._onKeydown(e);
    });

    this.addEventListener('keyup', (e) => {
      this._onKeyup(e);
    });

    this.addEventListener('click', (e) => {
      if (!e.composedPath().includes(this._wrapperDivEl)) {
        this._onClick(e);
      }
    });
  }

  updated(changedProps: PropertyValues) {
    if (changedProps.has('disabled')) {
      this._setTabIndex();
    }
  }

  private _setTabIndex() {
    if (this.disabled || this._hasWrappedAnchor) {
      this.removeAttribute('tabindex');
    } else {
      this.tabIndex = 0;
    }
  }

  static get styles() {
    return [super.styles, style];
  }

  render() {
    return html`
      <div part="button" @click="${this._onClick}" @keyup="${this._onKeyup}" @keydown="${this._onKeydown}"
        ><slot></slot
      ></div>
    `;
  }

  /**
   *
   * @param e The raw event that calls this method
   * @param shouldClickSelf an optional parameter that, if provided, will cause the click() function to be called on ZuiButton
   * @returns
   */
  private _onClick(e: Event, shouldClickSelf?: boolean) {
    if (this.disabled) {
      e.preventDefault();
      e.stopPropagation();
      return false;
    } else if (shouldClickSelf) {
      this.click();
    } else if (this._slottedControl && !e.composedPath().includes(this._slottedControl)) {
      this._slottedControl.click();
    }
  }

  private _onKeyup(e: KeyboardEvent) {
    if (clickKeys.includes(e.code)) {
      this._onClick(e, true);
    }
  }

  private _onKeydown(e: KeyboardEvent) {
    if (clickKeys.includes(e.code)) {
      if (!this.disabled) {
        this.focus();
        e.preventDefault();
        e.stopPropagation();
      }
    }
  }
}

window.customElements.define('zui-button', ZuiButton);

export type ZuiButtonType =
  | 'primary'
  | 'secondary'
  | 'link'
  | 'icon'
  | 'primary icon'
  | 'secondary icon'
  | 'link icon'
  | 'icon primary'
  | 'icon secondary'
  | 'icon link';

declare global {
  interface HTMLElementTagNameMap {
    'zui-button': ZuiButton;
  }
}
